import { useEffect, useMemo, useState } from "react";
import { PlusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Spin, Modal, Button, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import TableComponent from "../../components/TableComponent";
import TagComponent from "../../components/TagComponent";
import MainButtonComponent from "../../components/MainButtonComponent";
import SearchComponent from "../../components/SearchComponent";
import SelectComponent from "../../components/SelectComponent";
import { createColumn } from "../../utils/tableUtils";
import { tableDateFormatter } from "../../utils/formatDate";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	FiltersContainer,
	ActionButtonOverlay,
	StatusCell
} from "../../components/styled/common";
import { useEventContext } from "../../context/EventContext/EventContext";
import { EventListItem, VolunteersEventType, volunteersEventTypeLabels, eventStateColors } from "../../context/EventContext/constants";

const EventList: React.FC = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState<EventListItem | null>(null);
	const { eventList, clearEventList, clearEventFormData, getEventList, removeEvent, operationStatus } = useEventContext();

	const [searchText, setSearchText] = useState("");
	const [selectedEventType, setSelectedEventType] = useState<VolunteersEventType | null>(null);

	const navigate = useNavigate();

	const eventTypeOptions = useMemo(() =>
		Object.entries(volunteersEventTypeLabels).map(([value, label]) => ({ value: String(value), label })),
	[]);

	const tableData = useMemo(() => {
		const search = searchText.trim().toLocaleLowerCase("tr-TR");
		return eventList.filter((e) => {
			const headerMatch = !search || (e.header ?? "").toLocaleLowerCase("tr-TR").includes(search);
			const typeMatch = selectedEventType == null || e.eventType === selectedEventType;
			return headerMatch && typeMatch;
		});
	}, [eventList, searchText, selectedEventType]);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isDeleting = operationStatus.type === "loading" && operationStatus.operation === "delete";

	useEffect(() => {
		clearEventList();
		clearEventFormData();
		getEventList();

		return () => {
			clearEventList();
		};
	}, []);

	const columns = useMemo(() => [
		createColumn({
			title: "Sıra No",
			dataIndex: "displayOrder",
			width: 25,
			sorterType: "numeric",
		}),
		createColumn({
			title: "Başlık",
			dataIndex: "header",
			width: 120,
			sorterType: "string",
		}),
		createColumn({
			title: "Proje Tarihi",
			dataIndex: "projectDate",
			width: 40,
			sorterType: "string",
			render: (text, record) =>
				record.projectDate ? tableDateFormatter(record.projectDate, "DD.MM.YYYY") : (record.projectDateText || "-"),
		}),
		createColumn({
			title: "Proje Tipi",
			dataIndex: "eventType",
			width: 40,
			sorterType: "numeric",
			render: (text, record) => (
				<span>{volunteersEventTypeLabels[record.eventType as VolunteersEventType] ?? "-"}</span>
			),
		}),
		createColumn({
			title: "Kart Etiketi",
			dataIndex: "applicationStatus",
			width: 70,
			render: (text, record) => {
				const status = record.applicationStatus;
				if (!status) return "-";
				const color = eventStateColors[status.stateCode] ?? "grey";

				return (
					<TagComponent label={status.header || "-"} color={color} />
				);
			}
		}),
		createColumn({
			title: "Statü",
			dataIndex: "isActive",
			width: 35,
			sorterType: "boolean",
			render: (text, record) => (
				<StatusCell $bool={record.isActive}>{record.isActive ? "Aktif" : "Pasif"}</StatusCell>
			),
		}),
		createColumn({
			title: "Eklenme Tarihi",
			dataIndex: "createdDate",
			width: 40,
			sorterType: "string",
			render: (text, record) =>
				record.createdDate ? tableDateFormatter(record.createdDate, "DD.MM.YYYY") : "-",
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 20,
			render: (text, record: any) => {
				const menuItems = [
					{
						key: "edit",
						label: "Güncelle",
						onClick: () => navigate(`/events/edit/${record.eventId}`),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => {
							setCurrentItem(record);
							setIsModalVisible(true);
						},
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<MoreOutlined />
					</Dropdown>
				);
			},
		}),
	], [navigate]);

	return (
		<div>
			<HeaderComponent>Koç Gönüllüleri Projeleri</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>
					<ContainerHeader style={{ justifyContent: "space-between" }}>
						<FiltersContainer>
							<SearchComponent
								placeholder="Proje Başlığı Ara"
								style={{ width: "16vw" }}
								value={searchText}
								onChange={setSearchText}
							/>
							<SelectComponent
								allowClear
								placeholder="Proje Tipi Seç"
								style={{ width: "12vw" }}
								options={eventTypeOptions}
								value={selectedEventType != null ? String(selectedEventType) : null}
								onChange={(value) => setSelectedEventType(value != null ? Number(value) as VolunteersEventType : null)}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<MainButtonComponent
								style={{ marginRight: "15px" }}
								icon={<PlusCircleOutlined />}
								onClick={() => navigate("/events/add")}
							>
                                Yeni Ekle
							</MainButtonComponent>
						</ActionButtonOverlay>
					</ContainerHeader>
				</Spin>
				{!isFetching && (
					<TableComponent
						columns={columns}
						dataSource={tableData as any[]}
					/>
				)}
			</Container>
			<Modal
				title="Projeyi Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
                        İptal
					</Button>,
					<Button
						loading={isDeleting}
						key="submit"
						type="primary"
						danger
						onClick={async () => {
							if (currentItem) {
								await removeEvent(currentItem.eventId);
							}
							setIsModalVisible(false);
						}}
					>
                        Evet
					</Button>,
				]}
			>
				<p><strong>{`"${currentItem?.header ?? ""}"`}</strong> projesini silmek istediğinizden emin misiniz?</p>
			</Modal>
		</div>
	);
};

export default EventList;
